import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
import { composeItemDescriptionWithPlate } from "../../components/invoicesAndDocuments/invoicePlateStatement";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const appendQueryParams = (queryParams, params = {}) => {
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== "" && item != null && item !== undefined) {
          queryParams.append(`${key}[]`, String(item));
        }
      });
      return;
    }
    if (value !== "" && value != null && value !== undefined) {
      queryParams.append(key, String(value));
    }
  });
};

const itemTaxableFormValue = (item) =>
  item?.taxable === true || item?.taxable === 1 || item?.taxable === "1" ? "1" : "0";

export function buildPurchaseInvoiceFormData(form) {
  const fd = new FormData();

  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== "") {
      fd.append(key, String(value));
    }
  };

  [
    "winch_branch_id",
    "provider_type",
    "provider_id",
    "responsible_name",
    "service_type",
    "issue_date",
    "due_at",
    "cost_center_account_id",
    "tax_calculation",
    "tax_exemption_reason",
    "discount",
    "statement",
    "notes",
    "comment",
  ].forEach((key) => append(key, form[key]));

  append("status", form.status);

  const related = form.related_manual_invoices;
  if (Array.isArray(related)) {
    related.forEach((id, index) => {
      if (id !== "" && id != null) {
        fd.append(`related_manual_invoices[${index}]`, String(id));
      }
    });
  }

  (form.items || []).forEach((item, index) => {
    const fields = item.is_compensation
      ? [
          "supply_date",
          "compensation_category_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
          "tax_percentage",
          "parent_item_index",
        ]
      : [
          "supply_date",
          "service_type_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
          "tax_percentage",
        ];
    fields.forEach((field) => {
      if (item.is_compensation && field === "quantity") {
        fd.append(`items[${index}][quantity]`, "1");
        return;
      }
      if (field === "parent_item_index") {
        if (item.parent_item_index !== "" && item.parent_item_index != null) {
          fd.append(
            `items[${index}][parent_item_index]`,
            String(item.parent_item_index)
          );
        }
        return;
      }
      if (item[field] !== "" && item[field] != null) {
        fd.append(`items[${index}][${field}]`, String(item[field]));
      }
    });
    fd.append(`items[${index}][taxable]`, itemTaxableFormValue(item));
  });

  (form.attachments || []).forEach((file, index) => {
    if (file instanceof File) {
      fd.append(`attachments[${index}]`, file);
    }
  });

  return fd;
}

/** Build multipart body for POST /purchase_invoices/collected-travel */
export function buildCollectedTravelPurchaseInvoiceFormData(form) {
  const fd = new FormData();

  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== "") {
      fd.append(key, String(value));
    }
  };

  const appendAlways = (key, value) => {
    if (value !== null && value !== undefined) {
      fd.append(key, String(value));
    }
  };

  [
    "winch_branch_id",
    "provider_type",
    "provider_id",
    "issue_date",
    "due_at",
    "cost_center_account_id",
    "tax_calculation",
    "tax_exemption_reason",
    "statement",
    "notes",
    "comment",
    "table_direction",
    "merge_same_items",
    "show_plate_statement",
  ].forEach((key) => append(key, form[key]));

  appendAlways("responsible_name", form.responsible_name ?? "");
  append("discount", form.discount);
  append("status", form.status);

  ["show_statement", "show_totals", "show_city", "show_cr", "show_shipment_no_statement"].forEach(
    (key) => {
      if (form[key] !== null && form[key] !== undefined && form[key] !== "") {
        fd.append(key, String(form[key]));
      }
    }
  );

  const seenOrderIds = new Set();
  let orderIndex = 0;
  (form.items || []).forEach((item) => {
    const orderId = item?.order_id;
    if (orderId === "" || orderId == null) return;
    const key = String(orderId);
    if (seenOrderIds.has(key)) return;
    seenOrderIds.add(key);
    append(`items[${orderIndex}][order_id]`, orderId);
    append(
      `items[${orderIndex}][description]`,
      composeItemDescriptionWithPlate(item, form.show_plate_statement)
    );
    orderIndex += 1;
  });

  (form.attachments || []).forEach((file, index) => {
    if (file instanceof File) {
      fd.append(`attachments[${index}]`, file);
    }
  });

  return fd;
}

export const createPurchaseInvoiceManual = createAsyncThunk(
  "purchase_invoices/createManual",
  async (data, { rejectWithValue }) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && data instanceof FormData;
      const response = await axios.post(
        `${BASE_URL}/purchase_invoices/manual`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            ...(isFormData
              ? { "Content-Type": "multipart/form-data" }
              : { "Content-Type": "application/json" }),
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add data";
      return rejectWithValue(message);
    }
  }
);

export const createPurchaseInvoiceCollectedTravel = createAsyncThunk(
  "purchase_invoices/createCollectedTravel",
  async (data, { rejectWithValue }) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && data instanceof FormData;
      const response = await axios.post(
        `${BASE_URL}/purchase_invoices/collected-travel`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            ...(isFormData
              ? { "Content-Type": "multipart/form-data" }
              : { "Content-Type": "application/json" }),
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add data";
      return rejectWithValue(message);
    }
  }
);

/** Build multipart body for POST /purchase_invoices/collected-rental */
export function buildCollectedRentalPurchaseInvoiceFormData(form) {
  const fd = new FormData();

  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== "") {
      fd.append(key, String(value));
    }
  };

  const appendAlways = (key, value) => {
    if (value !== null && value !== undefined) {
      fd.append(key, String(value));
    }
  };

  [
    "winch_branch_id",
    "provider_type",
    "provider_id",
    "issue_date",
    "due_at",
    "cost_center_account_id",
    "tax_calculation",
    "tax_exemption_reason",
    "statement",
    "notes",
    "comment",
    "table_direction",
    "merge_same_items",
    "show_plate_statement",
  ].forEach((key) => append(key, form[key]));

  appendAlways("responsible_name", form.responsible_name ?? "");
  append("discount", form.discount);
  append("status", form.status);

  ["show_statement", "show_totals", "show_city", "show_cr", "show_shipment_no_statement"].forEach(
    (key) => {
      if (form[key] !== null && form[key] !== undefined && form[key] !== "") {
        fd.append(key, String(form[key]));
      }
    }
  );

  let itemIndex = 0;
  (form.items || []).forEach((item) => {
    const orderId = item?.order_id;
    if (orderId === "" || orderId == null) return;

    append(`items[${itemIndex}][order_id]`, orderId);
    append(`items[${itemIndex}][supply_date]`, item.supply_date);
    append(`items[${itemIndex}][service_type_id]`, item.service_type_id);
    append(
      `items[${itemIndex}][description]`,
      composeItemDescriptionWithPlate(item, form.show_plate_statement)
    );
    append(`items[${itemIndex}][quantity]`, item.quantity);
    append(`items[${itemIndex}][unit_price]`, item.unit_price);
    fd.append(
      `items[${itemIndex}][taxable]`,
      item?.taxable === true || item?.taxable === 1 || item?.taxable === "1"
        ? "1"
        : "0"
    );
    if (
      item?.taxable === true ||
      item?.taxable === 1 ||
      item?.taxable === "1"
    ) {
      append(`items[${itemIndex}][tax_percentage]`, item.tax_percentage || "15");
    }
    itemIndex += 1;
  });

  (form.attachments || []).forEach((file, index) => {
    if (file instanceof File) {
      fd.append(`attachments[${index}]`, file);
    }
  });

  return fd;
}

export const createPurchaseInvoiceCollectedRental = createAsyncThunk(
  "purchase_invoices/createCollectedRental",
  async (data, { rejectWithValue }) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && data instanceof FormData;
      const response = await axios.post(
        `${BASE_URL}/purchase_invoices/collected-rental`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            ...(isFormData
              ? { "Content-Type": "multipart/form-data" }
              : { "Content-Type": "application/json" }),
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add data";
      return rejectWithValue(message);
    }
  }
);

export const fetchPurchaseInvoices = createAsyncThunk(
  "purchase_invoices/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      appendQueryParams(queryParams, { page: 1, per_page: 20, ...params });
      const qs = queryParams.toString();
      const response = await axios.get(
        `${BASE_URL}/purchase_invoices${qs ? `?${qs}` : ""}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      return rejectWithValue(message);
    }
  }
);

export const fetchPurchaseInvoiceDetail = createAsyncThunk(
  "purchase_invoices/fetchDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/purchase_invoices/show/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data?.data ?? null;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load invoice";
      return rejectWithValue(message);
    }
  }
);

const postPurchaseInvoiceFormData = async (url, data, rejectWithValue) => {
  try {
    const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
    const response = await axios.post(url, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        ...(isFormData
          ? { "Content-Type": "multipart/form-data" }
          : { "Content-Type": "application/json" }),
        Lang: i18n.language,
      },
    });
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || "An unknown error occurred";
    return rejectWithValue(message);
  }
};

/** Build multipart body for POST /purchase_invoices/update-details */
export function buildUpdatePurchaseInvoiceDetailsFormData(form) {
  const fd = new FormData();

  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== "") {
      fd.append(key, String(value));
    }
  };

  const appendAlways = (key, value) => {
    if (value !== null && value !== undefined) {
      fd.append(key, String(value));
    }
  };

  appendAlways("id", form.id);
  appendAlways("responsible_name", form.responsible_name ?? "");
  append("issue_date", form.issue_date);
  append("due_at", form.due_at);
  append("tax_calculation", form.tax_calculation);
  appendAlways("tax_exemption_reason", form.tax_exemption_reason ?? "");
  appendAlways("statement", form.statement ?? "");
  appendAlways("notes", form.notes ?? "");
  appendAlways("comment", form.comment ?? "");
  append("status", form.status);
  appendAlways(
    "invoice_system",
    form.invoice_system === true || form.invoice_system === 1 || form.invoice_system === "1"
      ? "1"
      : "0"
  );
  appendAlways(
    "send_to_customer",
    form.send_to_customer === true ||
      form.send_to_customer === 1 ||
      form.send_to_customer === "1"
      ? "1"
      : "0"
  );
  append("table_direction", form.table_direction || "ar");
  ["show_statement", "show_totals", "show_city", "show_cr"].forEach((key) => {
    const val = form[key];
    appendAlways(key, val === true || val === 1 || val === "1" ? "1" : "0");
  });

  const newAttachments = (form.attachments || []).filter((file) => file instanceof File);
  appendAlways(
    "replace_attachments",
    form.replace_attachments === "1" || form.replace_attachments === 1 || newAttachments.length > 0
      ? "1"
      : "0"
  );

  newAttachments.forEach((file, index) => {
    fd.append(`attachments[${index}]`, file);
  });

  return fd;
}

/**
 * Unpaid + on_total (collected OR manual): POST /purchase_invoices/update-details
 * — limited fields + items[id] + items[description] only.
 */
export function buildUpdatePurchaseInvoiceUnpaidDetailsFormData(form, items = []) {
  const fd = new FormData();
  const appendAlways = (key, value) => {
    fd.append(key, value === null || value === undefined ? "" : String(value));
  };
  const flag01 = (value) =>
    value === true || value === 1 || value === "1" ? "1" : "0";

  appendAlways("id", form.id);
  appendAlways("responsible_name", form.responsible_name ?? "");
  appendAlways("issue_date", form.issue_date ?? "");
  appendAlways("due_at", form.due_at ?? "");
  appendAlways("statement", form.statement ?? "");
  appendAlways("notes", form.notes ?? "");
  appendAlways("table_direction", form.table_direction || "ar");
  appendAlways("show_statement", flag01(form.show_statement));
  appendAlways("show_totals", flag01(form.show_totals));
  appendAlways("show_city", flag01(form.show_city));
  appendAlways("show_cr", flag01(form.show_cr));
  appendAlways("merge_same_items", form.merge_same_items ?? "without_merge");
  appendAlways("show_plate_statement", form.show_plate_statement ?? "hide");
  appendAlways(
    "show_shipment_no_statement",
    flag01(form.show_shipment_no_statement)
  );

  (form.attachments || []).forEach((file, index) => {
    if (file instanceof File) {
      fd.append(`attachments[${index}]`, file);
    }
  });

  (Array.isArray(items) ? items : []).forEach((item, index) => {
    appendAlways(
      `items[${index}][id]`,
      item?.id != null && item.id !== "" ? item.id : ""
    );
    appendAlways(
      `items[${index}][description]`,
      item?.description != null ? item.description : ""
    );
  });

  return fd;
}

/** @deprecated use buildUpdatePurchaseInvoiceUnpaidDetailsFormData */
export const buildUpdatePurchaseInvoiceUnpaidCollectedDetailsFormData =
  buildUpdatePurchaseInvoiceUnpaidDetailsFormData;

/** Build multipart body for POST /purchase_invoices/update-manual-items */
export function buildUpdatePurchaseInvoiceManualItemsFormData(
  id,
  items = [],
  status = "draft"
) {
  const fd = new FormData();
  fd.append("id", String(id));
  fd.append("status", String(status || "draft"));

  items.forEach((item, index) => {
    const fields = item.is_compensation
      ? [
          "supply_date",
          "compensation_category_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
          "parent_item_index",
        ]
      : [
          "supply_date",
          "service_type_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
        ];
    fields.forEach((field) => {
      if (item.is_compensation && field === "quantity") {
        fd.append(`items[${index}][quantity]`, "1");
        return;
      }
      if (field === "parent_item_index") {
        if (item.parent_item_index !== "" && item.parent_item_index != null) {
          fd.append(
            `items[${index}][parent_item_index]`,
            String(item.parent_item_index)
          );
        }
        return;
      }
      if (item[field] !== "" && item[field] != null) {
        fd.append(`items[${index}][${field}]`, String(item[field]));
      }
    });
    fd.append(
      `items[${index}][taxable]`,
      item.taxable === true || item.taxable === 1 || item.taxable === "1" ? "1" : "0"
    );
  });

  return fd;
}

/**
 * Full multipart body for POST /purchase_invoices/update-manual-items
 * (details + items) — used when saving manual draft invoice details.
 */
export function buildUpdatePurchaseInvoiceManualFormData(form, items = []) {
  const fd = new FormData();
  const appendAlways = (key, value) => {
    fd.append(key, value === null || value === undefined ? "" : String(value));
  };
  const flag01 = (value) =>
    value === true || value === 1 || value === "1" ? "1" : "0";

  appendAlways("id", form.id);
  appendAlways("status", form.status ?? "unpaid");
  appendAlways("responsible_name", form.responsible_name ?? "");
  appendAlways("service_type", form.service_type ?? "");
  appendAlways("issue_date", form.issue_date ?? "");
  appendAlways("due_at", form.due_at ?? "");
  appendAlways("tax_calculation", form.tax_calculation ?? "");
  appendAlways("tax_exemption_reason", form.tax_exemption_reason ?? "");
  appendAlways("statement", form.statement ?? "");
  appendAlways("notes", form.notes ?? "");
  appendAlways("comment", form.comment ?? "");
  appendAlways("invoice_system", flag01(form.invoice_system));
  appendAlways("send_to_customer", flag01(form.send_to_customer));
  appendAlways("table_direction", form.table_direction || "ar");
  ["show_statement", "show_totals", "show_city", "show_cr"].forEach((key) => {
    appendAlways(key, flag01(form[key]));
  });
  appendAlways("merge_same_items", form.merge_same_items ?? "without_merge");
  appendAlways("show_plate_statement", form.show_plate_statement ?? "hide");
  appendAlways(
    "show_shipment_no_statement",
    flag01(form.show_shipment_no_statement ?? "0")
  );

  const related = form.related_manual_invoices;
  if (Array.isArray(related)) {
    related.forEach((invoiceId, index) => {
      if (invoiceId !== "" && invoiceId != null) {
        fd.append(`related_manual_invoices[${index}]`, String(invoiceId));
      }
    });
  }

  (items || []).forEach((item, index) => {
    const fields = item.is_compensation
      ? [
          "supply_date",
          "compensation_category_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
          "parent_item_index",
        ]
      : [
          "supply_date",
          "service_type_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
        ];
    fields.forEach((field) => {
      if (item.is_compensation && field === "quantity") {
        fd.append(`items[${index}][quantity]`, "1");
        return;
      }
      if (field === "parent_item_index") {
        if (item.parent_item_index !== "" && item.parent_item_index != null) {
          fd.append(
            `items[${index}][parent_item_index]`,
            String(item.parent_item_index)
          );
        }
        return;
      }
      if (item[field] !== "" && item[field] != null) {
        fd.append(`items[${index}][${field}]`, String(item[field]));
      }
    });
    fd.append(`items[${index}][taxable]`, flag01(item.taxable));
  });

  const newAttachments = (form.attachments || []).filter((file) => file instanceof File);
  appendAlways(
    "replace_attachments",
    form.replace_attachments === "1" ||
      form.replace_attachments === 1 ||
      newAttachments.length > 0
      ? "1"
      : "0"
  );
  newAttachments.forEach((file, index) => {
    fd.append(`attachments[${index}]`, file);
  });

  return fd;
}

/** Build multipart body for POST /purchase_invoices/update-collected-items */
export function buildUpdatePurchaseInvoiceCollectedItemsFormData(form, items = []) {
  const fd = new FormData();

  const appendAlways = (key, value) => {
    fd.append(key, value === null || value === undefined ? "" : String(value));
  };

  const flag01 = (value) =>
    value === true || value === 1 || value === "1" ? "1" : "0";

  appendAlways("id", form.id);
  appendAlways("status", form.status ?? "draft");
  appendAlways("responsible_name", form.responsible_name ?? "");
  appendAlways("service_type", form.service_type ?? "");
  appendAlways("issue_date", form.issue_date ?? "");
  appendAlways("due_at", form.due_at ?? "");
  appendAlways("tax_calculation", form.tax_calculation ?? "");
  appendAlways("tax_exemption_reason", form.tax_exemption_reason ?? "");
  appendAlways("statement", form.statement ?? "");
  appendAlways("notes", form.notes ?? "");
  appendAlways("comment", form.comment ?? "");
  appendAlways("invoice_system", flag01(form.invoice_system));
  appendAlways("send_to_customer", flag01(form.send_to_customer));
  appendAlways("table_direction", form.table_direction || "ar");
  appendAlways("merge_same_items", form.merge_same_items ?? "without_merge");
  appendAlways("show_plate_statement", form.show_plate_statement ?? "hide");

  ["show_statement", "show_totals", "show_city", "show_cr", "show_shipment_no_statement"].forEach(
    (key) => {
      appendAlways(key, flag01(form[key]));
    }
  );

  const related = form.related_manual_invoices;
  if (Array.isArray(related)) {
    related.forEach((invoiceId, index) => {
      if (invoiceId !== "" && invoiceId != null) {
        fd.append(`related_manual_invoices[${index}]`, String(invoiceId));
      }
    });
  }

  items
    .filter((item) => !item?.compensation_id)
    .forEach((item, index) => {
      if (item?.order_id != null && item.order_id !== "") {
        fd.append(`items[${index}][order_id]`, String(item.order_id));
      }
      fd.append(`items[${index}][taxable]`, flag01(item?.taxable));
    });
  const newAttachments = (form.attachments || []).filter((file) => file instanceof File);
  appendAlways(
    "replace_attachments",
    form.replace_attachments === "1" ||
      form.replace_attachments === 1 ||
      newAttachments.length > 0
      ? "1"
      : "0"
  );

  newAttachments.forEach((file, index) => {
    fd.append(`attachments[${index}]`, file);
  });

  return fd;
}




export const updatePurchaseInvoiceDetails = createAsyncThunk(
  "purchase_invoices/updateDetails",
  async (data, { rejectWithValue }) =>
    postPurchaseInvoiceFormData(
      `${BASE_URL}/purchase_invoices/update-details`,
      data,
      rejectWithValue
    )
);

export const updatePurchaseInvoiceManualItems = createAsyncThunk(
  "purchase_invoices/updateManualItems",
  async (data, { rejectWithValue }) =>
    postPurchaseInvoiceFormData(
      `${BASE_URL}/purchase_invoices/update-manual-items`,
      data,
      rejectWithValue
    )
);

export const updatePurchaseInvoiceCollectedItems = createAsyncThunk(
  "purchase_invoices/updateCollectedItems",
  async (data, { rejectWithValue }) =>
    postPurchaseInvoiceFormData(
      `${BASE_URL}/purchase_invoices/update-collected-items`,
      data,
      rejectWithValue
    )
);

export const publishPurchaseInvoice = createAsyncThunk(
  "purchase_invoices/publish",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/purchase_invoices/publish`,
        null,
        {
          params: { id: String(invoiceId) },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to publish invoice";
      return rejectWithValue(message);
    }
  }
);

export const deletePurchaseInvoice = createAsyncThunk(
  "purchase_invoices/delete",
  async (id, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("id", String(id));

      const response = await axios.post(
        `${BASE_URL}/purchase_invoices/delete`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return { ...(response.data || {}), id };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete invoice";
      return rejectWithValue(message);
    }
  }
);

export const releasePurchaseInvoicePaymentBond = createAsyncThunk(
  "purchase_invoices/releasePaymentBond",
  async ({ bond_id, purchase_invoice_id }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("bond_id", String(bond_id));
      formData.append("purchase_invoice_id", String(purchase_invoice_id));

      const response = await axios.post(
        `${BASE_URL}/bonds/remove-purchase-allocation`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to remove payment bond";
      return rejectWithValue(message);
    }
  }
);

export const releasePurchaseInvoiceDebitNote = createAsyncThunk(
  "purchase_invoices/releaseDebitNote",
  async ({ debit_note_id, purchase_invoice_id }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("debit_note_id", String(debit_note_id));
      formData.append("purchase_invoice_id", String(purchase_invoice_id));

      const response = await axios.post(
        `${BASE_URL}/debit-notes/remove-purchase-allocation`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to remove debit note allocation";
      return rejectWithValue(message);
    }
  }
);

export const allocatePurchaseInvoiceBond = createAsyncThunk(
  "purchase_invoices/allocateBond",
  async ({ invoiceId, bonds = [] }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("id", String(invoiceId));
      bonds.forEach((bond, index) => {
        formData.append(`bonds[${index}][bond_id]`, String(bond.bond_id));
        formData.append(
          `bonds[${index}][allocated_amount]`,
          String(bond.allocated_amount)
        );
      });

      const response = await axios.post(
        `${BASE_URL}/purchase_invoices/allocate-bond`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to allocate bond";
      return rejectWithValue(message);
    }
  }
);

const PurchaseInvoicesSlice = createSlice({
  name: "purchase_invoices",
  initialState: {
    purchase_invoices: [],
    pagination: null,
    record: null,
    isLoading: false,
    detailLoading: false,
    detailError: null,
    error: null,
    success: null,
    updateLoading: false,
    updateSuccess: null,
    publishLoading: false,
    deleteLoading: false,
    releasePaymentBondLoading: false,
    releaseDebitNoteLoading: false,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.updateSuccess = null;
    },
    clearPurchaseInvoiceDetail: (state) => {
      state.record = null;
      state.detailLoading = false;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseInvoices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPurchaseInvoices.fulfilled, (state, action) => {
        state.isLoading = false;
        const payloadData = action.payload?.data;
        state.purchase_invoices = Array.isArray(payloadData?.data)
          ? payloadData.data
          : Array.isArray(payloadData)
            ? payloadData
            : [];
        state.pagination = payloadData?.pagination || action.payload?.pagination || null;
      })
      .addCase(fetchPurchaseInvoices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(createPurchaseInvoiceManual.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPurchaseInvoiceManual.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Created successfully";
        const data = action.payload?.data ?? action.payload;
        const created =
          data && typeof data === "object" && !Array.isArray(data)
            ? data?.purchase_invoice && typeof data.purchase_invoice === "object"
              ? data.purchase_invoice
              : data?.invoice && typeof data.invoice === "object"
                ? data.invoice
                : data
            : null;
        if (created?.id != null) {
          state.record = created;
        }
      })
      .addCase(createPurchaseInvoiceManual.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(createPurchaseInvoiceCollectedTravel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPurchaseInvoiceCollectedTravel.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Created successfully";
        const data = action.payload?.data ?? action.payload;
        const created =
          data && typeof data === "object" && !Array.isArray(data)
            ? data?.purchase_invoice && typeof data.purchase_invoice === "object"
              ? data.purchase_invoice
              : data?.invoice && typeof data.invoice === "object"
                ? data.invoice
                : data
            : null;
        if (created?.id != null) {
          state.record = created;
        }
      })
      .addCase(createPurchaseInvoiceCollectedTravel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(createPurchaseInvoiceCollectedRental.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPurchaseInvoiceCollectedRental.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Created successfully";
        const data = action.payload?.data ?? action.payload;
        const created =
          data && typeof data === "object" && !Array.isArray(data)
            ? data?.purchase_invoice && typeof data.purchase_invoice === "object"
              ? data.purchase_invoice
              : data?.invoice && typeof data.invoice === "object"
                ? data.invoice
                : data
            : null;
        if (created?.id != null) {
          state.record = created;
        }
      })
      .addCase(createPurchaseInvoiceCollectedRental.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(fetchPurchaseInvoiceDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchPurchaseInvoiceDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchPurchaseInvoiceDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload || "Failed to load invoice";
      })
      .addCase(updatePurchaseInvoiceDetails.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updatePurchaseInvoiceDetails.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = action.payload?.message || "Updated";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(updatePurchaseInvoiceDetails.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || "Failed to update invoice";
      })
      .addCase(updatePurchaseInvoiceManualItems.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updatePurchaseInvoiceManualItems.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = action.payload?.message || "Updated";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(updatePurchaseInvoiceManualItems.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || "Failed to update manual items";
      })
      .addCase(updatePurchaseInvoiceCollectedItems.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updatePurchaseInvoiceCollectedItems.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = action.payload?.message || "Updated";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(updatePurchaseInvoiceCollectedItems.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || "Failed to update collected items";
      })
      .addCase(publishPurchaseInvoice.pending, (state) => {
        state.publishLoading = true;
        state.error = null;
      })
      .addCase(publishPurchaseInvoice.fulfilled, (state, action) => {
        state.publishLoading = false;
        state.updateSuccess = action.payload?.message || "Published";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(publishPurchaseInvoice.rejected, (state, action) => {
        state.publishLoading = false;
        state.error = action.payload || "Failed to publish invoice";
      })
      .addCase(deletePurchaseInvoice.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deletePurchaseInvoice.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.success = action.payload?.message || "Deleted successfully";
        const deletedId = action.payload?.id ?? action.meta?.arg;
        if (deletedId != null) {
          state.purchase_invoices = (state.purchase_invoices || []).filter(
            (row) => String(row?.id) !== String(deletedId)
          );
        }
        if (
          state.record &&
          String(state.record.id) === String(deletedId)
        ) {
          state.record = null;
        }
      })
      .addCase(deletePurchaseInvoice.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload || "Failed to delete invoice";
      })
      .addCase(releasePurchaseInvoicePaymentBond.pending, (state) => {
        state.releasePaymentBondLoading = true;
      })
      .addCase(releasePurchaseInvoicePaymentBond.fulfilled, (state, action) => {
        state.releasePaymentBondLoading = false;
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(releasePurchaseInvoicePaymentBond.rejected, (state, action) => {
        state.releasePaymentBondLoading = false;
        state.error = action.payload || "Failed to remove payment bond";
      })
      .addCase(releasePurchaseInvoiceDebitNote.pending, (state) => {
        state.releaseDebitNoteLoading = true;
      })
      .addCase(releasePurchaseInvoiceDebitNote.fulfilled, (state, action) => {
        state.releaseDebitNoteLoading = false;
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(releasePurchaseInvoiceDebitNote.rejected, (state, action) => {
        state.releaseDebitNoteLoading = false;
        state.error =
          action.payload || "Failed to remove debit note allocation";
      })
      .addCase(allocatePurchaseInvoiceBond.pending, (state) => {
        state.allocateBondLoading = true;
      })
      .addCase(allocatePurchaseInvoiceBond.fulfilled, (state, action) => {
        state.allocateBondLoading = false;
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(allocatePurchaseInvoiceBond.rejected, (state, action) => {
        state.allocateBondLoading = false;
        state.error = action.payload || "Failed to allocate bond";
      });
  },
});

export const { clearState, clearPurchaseInvoiceDetail } = PurchaseInvoicesSlice.actions;
export default PurchaseInvoicesSlice.reducer;
